/**
 * Carries the Thank-You receipt across the post-subscribe page load.
 *
 * WHY THIS EXISTS
 *   Finishing a first subscribe (or recovering from the lapsed paywall) needs a full document
 *   load: that is what lifts the paywall, since it re-reads `me` and drops every Apollo query and
 *   company-scoped store. The wizard used to do that load when the user CLOSED the Thank-You, so
 *   the last thing they saw was the confirmation being torn down and the page reloading.
 *
 *   So the load moved INTO the transaction instead: charge succeeds → receipt is stashed here →
 *   the document is replaced while the button still reads "Processing" → the fresh document
 *   restores the receipt and shows the Thank-You as the confirmation that everything went
 *   through. Closing it is then instant, because the page behind it is already the new one.
 *
 * WHY sessionStorage AND NOT ROUTER STATE
 *   The existing iDEAL/Bancontact restore (`redirectChargeResult` in SubscriptionPage) passes the
 *   receipt through React Router state, which does not survive `location.assign`. sessionStorage
 *   does, is same-tab only, and is cleared by the browser when the tab closes.
 *
 * The read is destructive and time-boxed: a receipt is shown exactly once, and a stale one (tab
 * left open, navigated back days later) is dropped rather than resurfacing a confirmation for a
 * purchase the user has long since forgotten.
 */
import { safeJsonParse } from '@/lib/safe-format';

const KEY = 'scio3.postSubscribeThankYou';
/** #2046 — a plain confirmation line, for the flows that navigate instead of showing a receipt. */
const TOAST_KEY = 'scio3.postSubscribeToast';
/** Long enough for a slow document load, far short of "later in the session". */
const MAX_AGE_MS = 2 * 60 * 1000;

type Stashed = {
  chargeResult?: Record<string, unknown>;
  at?: number;
};

/** Called at charge success, immediately before the navigation. */
export function stashPostSubscribeThankYou(chargeResult: Record<string, unknown>): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ chargeResult, at: Date.now() } satisfies Stashed));
  } catch {
    // Private mode / quota. The user still lands subscribed on a fresh page; they just do not
    // get the Thank-You. Never let a storage failure break the purchase flow.
  }
}

/**
 * Read-and-clear. Returns the receipt once, then never again for that purchase.
 *
 * Cleared BEFORE the age check so a stale entry cannot sit there being re-evaluated on every
 * subsequent page load.
 */
export function takePostSubscribeThankYou(): Record<string, unknown> | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    if (raw != null) sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  const parsed = safeJsonParse<Stashed>(raw, {});
  const { chargeResult, at } = parsed;
  if (!chargeResult || typeof at !== 'number') return null;
  if (Date.now() - at > MAX_AGE_MS) return null;
  return chargeResult;
}

/**
 * #2046 — carries a one-line confirmation across the same document swap.
 *
 * The downgrade-to-Free flows toast "Your plan has been downgraded to Free" and then replace the
 * document, which destroys the toast before it can be read: the user is told nothing, lands on a
 * fresh page, and has to visit Subscription Plan to learn whether the downgrade worked. A receipt
 * would be too much for a downgrade, so the message rides across on its own and is re-toasted on
 * the far side by `BillingDialogs`.
 *
 * Same rules as the receipt above: sessionStorage (survives `location.assign`, same-tab only),
 * destructive read, and time-boxed so a stale entry cannot congratulate someone days later.
 */
export function stashPostSubscribeToast(message: string): void {
  try {
    sessionStorage.setItem(TOAST_KEY, JSON.stringify({ message, at: Date.now() }));
  } catch {
    // Private mode / quota. The downgrade still went through; the user just loses the line.
  }
}

/** Read-and-clear counterpart of `stashPostSubscribeToast`. */
export function takePostSubscribeToast(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(TOAST_KEY);
    if (raw != null) sessionStorage.removeItem(TOAST_KEY);
  } catch {
    return null;
  }
  const { message, at } = safeJsonParse<{ message?: string; at?: number }>(raw, {});
  if (!message || typeof at !== 'number') return null;
  if (Date.now() - at > MAX_AGE_MS) return null;
  return message;
}
